// src/components/student/VideoFeedbackPage.js
//
// Video feedback page — 2-column layout, warm light theme.
// Left: XP banner, breadcrumb, compact video player with timestamp dots, info, analysis, drills.
// Right: sticky coach feedback panel with timestamped comments.

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../AuthContext';
import { db } from '../../firebase';
import { subscribeTimestampedFeedback } from '../../utils/videoFeedback';
import { buildThreadId, getThreadNavigationTarget } from '../../utils/threadNavigation';
import { buildMovementAnalysisView } from '../../utils/reviewAnalysis';
import StudentNav from '../shared/StudentNav';
import MotusIcon from '../shared/MotusIcon';

const C = {
  bg:'#FAFAF8', card:'#FFFFFF', card2:'#F5F4F0', card3:'#ECEAE4',
  border:'rgba(0,0,0,0.06)', borderH:'rgba(0,0,0,0.12)',
  blue:'#2563EB', blueDim:'rgba(37,99,235,0.08)',
  green:'#22C55E', greenDim:'rgba(34,197,94,0.08)',
  orange:'#F05A1F', orangeDim:'rgba(240,90,31,0.08)',
  purple:'#9333EA', purpleDim:'rgba(147,51,234,0.08)',
  gold:'#D97706', goldDim:'rgba(217,119,6,0.08)',
  red:'#DC2626', t1:'#1C1B18', t2:'#6B6B63', t3:'#A3A299',
};
const font = "'DM Sans',sans-serif";
const fontD = "'Bebas Neue',sans-serif";

const STEP_COLORS = {
  detect: { color:C.blue, bg:C.blueDim, label:'Detect', icon:'search' },
  cause: { color:C.orange, bg:C.orangeDim, label:'Cause', icon:'zap' },
  effect: { color:C.red, bg:'rgba(220,38,38,0.06)', label:'Effect', icon:'warning' },
  correct: { color:C.green, bg:C.greenDim, label:'Correct', icon:'check' },
  train: { color:C.purple, bg:C.purpleDim, label:'Train', icon:'target' },
  text: { color:C.blue, bg:C.blueDim, label:'Comment', icon:'message' },
};

function VideoFeedbackPage() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const videoRef = useRef(null);

  const [video, setVideo] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [activeComment, setActiveComment] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);

  // Pass 73 (§44/§76) — TWO independent subscriptions: the video doc and the feedback timeline (via the ONE
  // shared reader). Adding/removing a comment no longer refetches the whole video, and a permission failure is
  // reported truthfully instead of collapsing into a permanent "Loading…".
  useEffect(() => {
    if (!videoId || !currentUser?.uid) return undefined;
    setLoading(true); setLoadError(''); setNotFound(false);
    const unsubVideo = onSnapshot(
      doc(db, 'videos', videoId),
      (snap) => {
        if (!snap.exists()) { setNotFound(true); setLoading(false); return; }
        setVideo({ id: snap.id, ...snap.data() });
        setLoading(false);
      },
      (err) => { console.error('Video load failed:', err); setLoadError(err?.code === 'permission-denied' ? 'not-authorized' : 'load-failed'); setLoading(false); },
    );
    const unsubFeedback = subscribeTimestampedFeedback(db, videoId, setFeedback, (err) => {
      console.error('Feedback load failed:', err);
    });
    return () => { unsubVideo(); unsubFeedback(); };
  }, [videoId, currentUser]);

  const title = video?.videoName || video?.title || 'Untitled';
  const sport = video?.sport || 'ski';
  const videoUrl = video?.videoURL || video?.videoUrl || '';
  const isReviewed = video?.status === 'reviewed';
  // Pass 76/76A — the ONE pure read model over the canonical HUMAN movement analysis (video.analysis). It is
  // PRIMARY and AI-INDEPENDENT (renders whenever a reviewed video has meaningful structured analysis, with or
  // without AI). `analysisView.publishedAi` is the ONLY student-visible AI (approved + published `video.aiReview`,
  // reviewed-only; never a private draft) and is rendered EXCLUSIVELY inside the separate "AI-Assisted Notes"
  // section — never inside any human-coach-labelled surface (Pass 76A human/AI boundary repair).
  const analysisView = buildMovementAnalysisView(video);
  const humanReviewDrills = Array.isArray(video?.reviewDrills) ? video.reviewDrills : [];

  const seekTo = (seconds) => {
    if (videoRef.current) { videoRef.current.currentTime = seconds; videoRef.current.play(); }
  };

  const reviewAudioURL = video?.reviewAudioURL || '';
  // Pass 75 (§34) — the "Reply / Message" CTA deep-links to the EXACT deterministic review thread (never mints a
  // second conversation). Only meaningful once reviewed (the thread is created on review delivery).
  const replyTarget = (isReviewed && video?.reviewedBy && currentUser?.uid)
    ? getThreadNavigationTarget(buildThreadId(video.id, video.reviewedBy, currentUser.uid))
    : '/inbox?tab=messages';

  // Pass 73 (§45) — truthful states: never a permanent spinner after a permission failure, never another user's content.
  if (loading) return <StatusScreen>Loading…</StatusScreen>;
  if (notFound) return <StatusScreen>We couldn’t find that video.</StatusScreen>;
  if (loadError === 'not-authorized') return <StatusScreen>You don’t have access to this video.</StatusScreen>;
  if (loadError) return <StatusScreen>We couldn’t load this review right now. Please try again.</StatusScreen>;

  return (
    <div style={{ minHeight:'100vh', background:C.bg, color:C.t1, fontFamily:font }}>
      <div style={{ position:'fixed', inset:0, pointerEvents:'none', zIndex:0, background:'radial-gradient(ellipse 60% 40% at 5% 0%,rgba(37,99,235,0.03),transparent 55%)' }} />

      <StudentNav activeTab="videos" />

      {/* TWO COLUMNS */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 380px', minHeight:'calc(100vh - 60px)' }}>

        {/* LEFT */}
        <div style={{ padding:'24px 30px 60px', borderRight:`1px solid ${C.border}`, overflowY:'auto', position:'relative', zIndex:1 }}>

          {/* XP banner */}
          {isReviewed && (
            <div style={{ background:'linear-gradient(135deg,#F0FFF4,#ECFDF5)', border:'1px solid rgba(34,197,94,0.15)', borderRadius:14, padding:'12px 18px', display:'flex', alignItems:'center', gap:12, marginBottom:18 }}>
              <MotusIcon name="trophy" size={20} color={C.green} />
              <div style={{ flex:1, fontSize:13, color:C.t2 }}>This video has been <strong style={{ color:C.green }}>reviewed</strong> by your coach. Check the feedback on the right.</div>
              <div style={{ background:C.greenDim, border:'1px solid rgba(34,197,94,0.20)', color:C.green, fontSize:12, fontWeight:800, padding:'4px 12px', borderRadius:20 }}>✓ Reviewed</div>
            </div>
          )}

          {/* Breadcrumb */}
          <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:C.t3, marginBottom:16 }}>
            <span style={{ cursor:'pointer' }} onClick={() => navigate('/dashboard')}>Dashboard</span>
            <span>›</span>
            <span style={{ cursor:'pointer' }} onClick={() => navigate('/dashboard/videos')}>My Videos</span>
            <span>›</span>
            <span style={{ color:C.t1, fontWeight:600 }}>{title}</span>
          </div>

          {/* VIDEO PLAYER — compact, capped height for portrait videos */}
          <div style={{ borderRadius:16, overflow:'hidden', position:'relative', maxWidth:640, maxHeight:400, marginBottom:16, border:`1px solid ${C.border}`, boxShadow:'0 8px 24px rgba(0,0,0,0.04)', background:'#000' }}>
            {videoUrl ? (
              <video ref={videoRef} src={videoUrl} controls style={{ width:'100%', maxHeight:400, display:'block', objectFit:'contain', background:'#000' }}
                onLoadedMetadata={e => setVideoDuration(e.target.duration || 0)} />
            ) : (
              <div style={{ width:'100%', height:300, background:C.card2, display:'flex', alignItems:'center', justifyContent:'center', opacity:0.3 }}><MotusIcon name="video" size={40} color={C.t3} /></div>
            )}

            {/* Timestamp dots on timeline */}
            {videoDuration > 0 && feedback.length > 0 && (
              <div style={{ position:'absolute', bottom:38, left:12, right:12, height:0 }}>
                {feedback.map(item => {
                  const pct = ((item.timestamp || 0) / videoDuration) * 100;
                  const stepType = (item.type || 'text').toLowerCase();
                  const dotColor = STEP_COLORS[stepType]?.color || C.blue;
                  return (
                    <div key={item.id} onClick={(e) => { e.stopPropagation(); seekTo(item.timestamp); setActiveComment(item.id); }}
                      style={{ position:'absolute', left:`${pct}%`, top:-5, width:10, height:10, borderRadius:'50%', background:dotColor, border:'2px solid white', cursor:'pointer', transform:'translateX(-50%)', zIndex:5, boxShadow:'0 1px 3px rgba(0,0,0,0.2)', transition:'transform .15s' }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'translateX(-50%) scale(1.4)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'translateX(-50%)'} />
                  );
                })}
              </div>
            )}
          </div>

          {/* Video info */}
          <div style={{ marginBottom:22, maxWidth:720 }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:14, marginBottom:6 }}>
              <div style={{ fontFamily:fontD, fontSize:26, letterSpacing:1.5, color:C.t1, lineHeight:1 }}>{title}</div>
              {isReviewed && <span style={{ padding:'4px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:C.greenDim, border:'1px solid rgba(34,197,94,0.20)', color:C.green, flexShrink:0 }}>✓ Reviewed</span>}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:9, fontSize:13, color:C.t3 }}>
              <span>{sport}</span>
              <span>·</span>
              <span>{feedback.length} comment{feedback.length !== 1 ? 's' : ''}</span>
            </div>
          </div>

          {/* MOVEMENT ANALYSIS — the coach's structured HUMAN analysis (video.analysis). Primary, AI-independent.
              Pass 76 — driven by the canonical read model; sport-specific field names come from the schema, not
              hardcoded here. Shown only for a reviewed video with meaningful analysis. */}
          {analysisView.hasHumanAnalysis ? (
            <div data-testid="movement-analysis" style={{ marginBottom:24, maxWidth:640 }}>
              <div style={{ fontFamily:fontD, fontSize:20, letterSpacing:1.5, color:C.t1, marginBottom:4 }}>Movement Analysis</div>
              <div style={{ fontSize:12, color:C.t3, marginBottom:12 }}>{analysisView.display?.studentDescription || 'Structured analysis from your coach using Detect → Cause → Effect → Correct → Train.'}</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {analysisView.sections.map((section, i) => {
                  const step = STEP_COLORS[section.key] || STEP_COLORS.detect;
                  const spanFull = section.key === 'train' || (i === analysisView.sections.length - 1 && analysisView.sections.length % 2 === 1);
                  return (
                    <div key={section.key} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'13px 15px', gridColumn: spanFull ? '1/-1' : undefined }}>
                      <div style={{ fontSize:10, fontWeight:800, letterSpacing:2, textTransform:'uppercase', color:step.color, marginBottom:6, display:'flex', alignItems:'center', gap:4 }}>{step.icon && <MotusIcon name={step.icon} size={12} color={step.color} />}{section.title}</div>
                      {section.inline ? <div style={{ fontSize:13, color:C.t1, lineHeight:1.6, marginBottom:6 }}>{section.inline}</div> : null}
                      {section.chips.length > 0 && (
                        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:6 }}>
                          {section.chips.map((chip, ci) => (
                            <span key={ci} style={{ fontSize:11, fontWeight:600, color:step.color, background:step.bg, border:`1px solid ${step.color}25`, borderRadius:16, padding:'3px 9px' }}>{chip}</span>
                          ))}
                        </div>
                      )}
                      {section.stanceIssues.length > 0 && (
                        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:6 }}>
                          {section.stanceIssues.map((issue, si) => (
                            <span key={si} style={{ fontSize:11, color:C.t2, background:C.card3, borderRadius:16, padding:'3px 9px' }}>{issue.label}: {issue.value}</span>
                          ))}
                        </div>
                      )}
                      {section.texts.map((text, ti) => (
                        <div key={ti} style={{ fontSize:13, color:C.t1, lineHeight:1.6, marginTop: ti === 0 ? 0 : 6 }}>{text}</div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : isReviewed ? (
            <div style={{ marginBottom:24, maxWidth:640 }}>
              <div style={{ fontFamily:fontD, fontSize:20, letterSpacing:1.5, color:C.t1, marginBottom:12 }}>Movement Analysis</div>
              <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'14px 15px', fontSize:13, color:C.t3, lineHeight:1.6 }}>
                Your coach's feedback for this review is in the timestamped comments{reviewAudioURL ? ' and the audio note' : ''} on the right.
              </div>
            </div>
          ) : null}

          {/* AI-ASSISTED NOTES — optional, coach-approved & published supplement. Clearly separate from the human
              movement analysis above; only shown when actually published (never a private/unapproved draft). */}
          {analysisView.publishedAi && (analysisView.publishedAi.summary || analysisView.publishedAi.coachingNotes?.length > 0) ? (
            <div data-testid="ai-assisted-notes" style={{ marginBottom:24, maxWidth:640 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                <div style={{ fontFamily:fontD, fontSize:20, letterSpacing:1.5, color:C.t1 }}>AI-Assisted Notes</div>
                <span style={{ fontSize:10, fontWeight:700, letterSpacing:1, textTransform:'uppercase', color:C.purple, background:C.purpleDim, border:'1px solid rgba(147,51,234,0.18)', borderRadius:16, padding:'2px 8px' }}>Optional · coach-approved</span>
              </div>
              <div style={{ fontSize:12, color:C.t3, marginBottom:12 }}>Supplemental AI notes your coach reviewed and approved. Your coach's movement analysis above is the primary feedback.</div>
              {analysisView.publishedAi.coachingNotes?.length > 0 ? (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {analysisView.publishedAi.coachingNotes.slice(0, 5).map((note, i) => {
                    const key = ['detect','cause','effect','correct','train'][i] || 'detect';
                    const step = STEP_COLORS[key];
                    return (
                      <div key={i} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'13px 15px', gridColumn: i === 4 ? '1/-1' : undefined }}>
                        <div style={{ fontSize:10, fontWeight:800, letterSpacing:2, textTransform:'uppercase', color:step.color, marginBottom:5, display:'flex', alignItems:'center', gap:4 }}>{step.icon && <MotusIcon name={step.icon} size={12} color={step.color} />}{step.label}</div>
                        <div style={{ fontSize:13, color:C.t1, lineHeight:1.6 }}>{note}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'13px 15px', fontSize:13, color:C.t2, lineHeight:1.6 }}>{analysisView.publishedAi.summary}</div>
              )}
            </div>
          ) : null}

          {/* Drills — the coach's HUMAN drill selections (video.reviewDrills). Optional AI-suggested drills, when
              published, are shown under AI-Assisted Notes, not presented as the coach's own recommendation. */}
          <div style={{ maxWidth:640, marginBottom:24 }}>
            <div style={{ fontFamily:fontD, fontSize:20, letterSpacing:1.5, color:C.t1, marginBottom:12 }}>Recommended Drills</div>
            {humanReviewDrills.length > 0 ? humanReviewDrills.map((drill, i) => (
              <div key={i} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'13px 15px', display:'flex', alignItems:'center', gap:12, marginBottom:8, cursor:'pointer', transition:'all .18s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderH; e.currentTarget.style.transform = 'translateX(4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ width:40, height:40, borderRadius:10, background: i===0 ? C.blueDim : C.greenDim, border:`1px solid ${i===0 ? 'rgba(37,99,235,0.12)' : 'rgba(34,197,94,0.12)'}`, display:'flex', alignItems:'center', justifyContent:'center' }}><MotusIcon name="target" size={18} color={i===0 ? C.blue : C.green} /></div>
                <div style={{ flex:1 }}>
                  {/* Pass 58C — removed the fabricated "+25 XP" (drills award no XP; progression is capture-
                      derived) and the non-functional "Start →" button (no handler). Recommended drills are
                      informational until the drill-delivery flow ships. */}
                  <div style={{ fontSize:13, fontWeight:700, color:C.t1 }}>{typeof drill === 'string' ? drill : drill.title || 'Drill'}</div>
                  {typeof drill !== 'string' && drill.focus ? <div style={{ fontSize:11, color:C.t3 }}>{drill.focus}</div> : null}
                </div>
              </div>
            )) : (
              <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'20px 15px', textAlign:'center', fontSize:13, color:C.t3 }}>
                {isReviewed ? 'Your coach did not add specific drills to this review.' : 'Drills will be recommended after your coach reviews this video'}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Coach Feedback */}
        <div data-testid="coach-feedback-panel" style={{ display:'flex', flexDirection:'column', background:C.card, borderLeft:`1px solid ${C.border}`, height:'calc(100vh - 60px)', position:'sticky', top:60, overflow:'hidden' }}>

          {/* Header */}
          <div style={{ padding:'18px 20px 12px', borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
            <div style={{ fontFamily:fontD, fontSize:20, letterSpacing:1.5, color:C.t1, marginBottom:3 }}>Coach Feedback</div>
            {/* Pass 76A — Coach Feedback is HUMAN evidence ONLY. The former "· AI review" suffix was removed: AI
                content lives exclusively under the separate "AI-Assisted Notes" section, never mixed into a
                human-coach-labelled surface. */}
            <div style={{ fontSize:12, color:C.t3 }}>{feedback.length} timestamped comment{feedback.length !== 1 ? 's' : ''}{reviewAudioURL ? ' · audio note' : ''}</div>
          </div>

          {/* Pass 73 (§43) — the coach's optional review-level audio note. Only rendered when actually present. */}
          {reviewAudioURL ? (
            <div style={{ padding:'14px 20px', borderBottom:`1px solid ${C.border}`, flexShrink:0, background:C.card2 }}>
              <div style={{ display:'flex', alignItems:'center', gap:7, fontSize:11, fontWeight:700, letterSpacing:0.5, textTransform:'uppercase', color:C.t3, marginBottom:8 }}>
                <MotusIcon name="message" size={13} color={C.blue} /> Coach audio note
              </div>
              <audio controls preload="none" src={reviewAudioURL} style={{ width:'100%' }} />
            </div>
          ) : null}

          {/* Coach strip — always show */}
          <div style={{ display:'flex', alignItems:'center', gap:11, padding:'13px 20px', borderBottom:`1px solid ${C.border}`, background:C.card2, flexShrink:0 }}>
            <div style={{ width:40, height:40, borderRadius:'50%', background:`linear-gradient(135deg,${C.blue},${C.purple})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, fontWeight:700, color:'white', flexShrink:0 }}>
              {video?.reviewedByName ? video.reviewedByName.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2) : 'SC'}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:14, fontWeight:700, color:C.t1 }}>{video?.reviewedByName || 'Coach'}</div>
              <div style={{ fontSize:12, color:C.t3 }}>Reviewed this video</div>
            </div>
            {/* Pass 76 — the legacy quickCapture-derived "Score" chip was removed here: the student review page
                shows the coach's review evidence (movement analysis + timestamped/audio feedback), not a
                progression score. Progression lives on the canonical /me/progress surface. */}
          </div>

          {/* Pass 76A — the former "Overall Note" card is GONE. It sourced its text from `aiReview.summary`, which
              presented AI-generated text as a human coach note (a human/AI truth violation). There is NO canonical
              human overall-note field, so we do NOT fabricate one from AI. In its place: a NEUTRAL guidance strip
              that makes no claim of a coach-authored note and simply points the student at the real human evidence
              (timestamped comments, audio, and the Movement Analysis on the left). */}
          <div style={{ padding:'12px 20px', borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
            <div style={{ background:C.card2, border:`1px solid ${C.border}`, borderRadius:12, padding:'11px 13px', fontSize:12, color:C.t3, lineHeight:1.6 }}>
              {feedback.length > 0
                ? `Your coach left ${feedback.length} timestamped comment${feedback.length !== 1 ? 's' : ''}${reviewAudioURL ? ' and an audio note' : ''} — open each below. See the Movement Analysis on the left.`
                : (isReviewed
                    ? `Your coach's feedback for this review is in the Movement Analysis on the left${reviewAudioURL ? ' and the audio note above' : ''}.`
                    : 'Feedback will appear here once your coach reviews this video.')}
            </div>
          </div>

          {/* Comments */}
          <div style={{ flex:1, overflowY:'auto', padding:'12px 20px' }}>
            {feedback.length > 0 ? feedback.map((item) => {
              const ts = item.timestamp || 0;
              const timeStr = `${Math.floor(ts / 60)}:${String(Math.floor(ts % 60)).padStart(2, '0')}`;
              const stepType = (item.type || 'text').toLowerCase();
              const step = STEP_COLORS[stepType] || STEP_COLORS.text;
              const isActive = activeComment === item.id;
              const commentText = item.content || item.comment || item.text || item.note || '';

              return (
                <div key={item.id} onClick={() => { setActiveComment(item.id); seekTo(ts); }}
                  style={{ background:C.card2, border:`1px solid ${isActive ? C.blue : C.border}`, borderRadius:12, padding:'12px 14px', marginBottom:10, cursor:'pointer', transition:'all .2s', boxShadow: isActive ? `0 0 0 2px ${C.blueDim}` : 'none' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:7 }}>
                    <span onClick={e => { e.stopPropagation(); seekTo(ts); }}
                      style={{ fontFamily:"'DM Mono',monospace", fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:20, background:step.bg, color:step.color, border:`1px solid ${step.color}25`, cursor:'pointer' }}>⏱ {timeStr}</span>
                    <span style={{ fontSize:10, fontWeight:800, letterSpacing:1.5, textTransform:'uppercase', color:step.color }}>{step.label.split(' ').pop()}</span>
                  </div>
                  {commentText && <div style={{ fontSize:13, color:C.t1, lineHeight:1.6, marginBottom:9 }}>{commentText}</div>}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end' }}>
                    {/* Pass 58C — removed the non-functional "Audio" button (no handler). Reply is real. */}
                    <button onClick={e => { e.stopPropagation(); navigate(replyTarget); }}
                      style={{ fontSize:11, color:C.blue, fontWeight:600, cursor:'pointer', background:'none', border:'none', fontFamily:font }}>Reply</button>
                  </div>
                </div>
              );
            }) : (
              <div style={{ padding:'40px 0', textAlign:'center', color:C.t3, fontSize:13 }}>
                {isReviewed ? 'No timestamped comments yet' : 'Feedback will appear here once your coach reviews this video'}
              </div>
            )}
          </div>

          {/* Reply box */}
          <div style={{ padding:'10px 20px 16px', borderTop:`1px solid ${C.border}` }}>
            <div style={{ display:'flex', alignItems:'flex-end', gap:8, background:C.card2, border:`1px solid ${C.border}`, borderRadius:10, padding:'8px 12px' }}>
              <textarea placeholder="Ask your coach a question…" rows={1}
                style={{ flex:1, background:'none', border:'none', outline:'none', fontFamily:font, fontSize:13, color:C.t1, resize:'none', maxHeight:60, lineHeight:1.5 }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); navigate(replyTarget); }}} />
              <button onClick={() => navigate(replyTarget)}
                style={{ width:30, height:30, background:C.blue, border:'none', borderRadius:7, color:'white', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>➜</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusScreen({ children }) {
  return (
    <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', color:C.t3, fontFamily:font, padding:24, textAlign:'center' }}>
      {children}
    </div>
  );
}

export default VideoFeedbackPage;
